import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import { z } from "zod";

import LoadingSpinner from "@/components/common/common/LoadingSpinner";
import NoData from "@/components/common/common/Nodata";
import DraggableTable from "@/components/console/common/DraggableTable";
import Pagination from "@/components/console/common/Pagination";
import Checkbox from "@/components/console/form/Checkbox";
import ListSizeSelect from "@/components/console/form/ListSizeSelect";
import SearchInput from "@/components/console/form/SearchInput";
import SelectBox from "@/components/console/form/SelectBox";
import { IBoardListParams, listSearchTypes, listSizes } from "@/constants/common/listParams";
import usePagination from "@/hooks/common/usePagination";
import { useUrlParams } from "@/hooks/common/useUrlParams";
import useCheckboxList from "@/hooks/console/useCheckboxList";
import { useListSize } from "@/hooks/console/useListSize";
import { useDelBoard, useGetBoardList, usePutBoardNotice, usePutBoardOrder } from "@/service/console/board";
import { usePopupStore } from "@/store/common/usePopupStore";
import { makeIntComma } from "@/utils/numberUtils";

const schema = z.object({
    search: z.string(),
    searchtxt: z.string().optional(),
});

type SearchValues = z.infer<typeof schema>;

export interface BoardItem {
    idx: number;
    num: number | string;
    category: number;
    b_title: string;
    m_name: string;
    b_reg_date: string;
    b_notice: string;
    b_num: number;
}

export default function BoardList() {
    const { category } = useParams();
    const prevCategoryRef = useRef(category);
    const [items, setItems] = useState<BoardItem[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    // URL 파라미터 관리
    const { params, updateParams, resetParams } = useUrlParams<IBoardListParams>({
        listSize: { defaultValue: 10, type: "number", validValues: listSizes },
        page: { defaultValue: 1, type: "number" },
        search: { defaultValue: "title", type: "string", validValues: listSearchTypes.map(type => type.value) },
        searchtxt: { defaultValue: "", type: "string" },
    });
    const { listSize, options, setListSize } = useListSize({ defaultSize: 10 });
    const { currentPage, pages, setCurrentPage } = usePagination({ totalPages, initialPage: params.page });
    const { checkedNum, allCheck, setCheckList, checkedList, setCheckedList, handleAllCheck, handleCheck } =
        useCheckboxList();
    const { control, setValue, register } = useForm<SearchValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            search: params.search,
            searchtxt: params.searchtxt,
        },
    });
    const searchFilterValues = useWatch({ control });
    const {
        data: configData,
        isLoading: isInitialLoading,
        refetch,
    } = useGetBoardList(
        category || "",
        params.listSize.toString(),
        params.page.toString(),
        params.search,
        params.searchtxt,
        { enabled: Boolean(category) },
    );
    const putBoardNoticeMutation = usePutBoardNotice();
    const delBoardMutation = useDelBoard();
    const putBoardOrderMutation = usePutBoardOrder();
    const { setLoadingPop, setConfirmPop } = usePopupStore();

    // 파라미터 초기화
    useEffect(() => {
        // category가 변경되었을 때만 실행
        if (prevCategoryRef.current !== category) {
            resetParams();
            setCurrentPage(1);
            setValue("search", "title");
            setValue("searchtxt", "");

            // 현재 category를 이전 값으로 저장
            prevCategoryRef.current = category;
        }
    }, [category, resetParams, setCurrentPage, setValue]);

    // listSize 변경 시
    const handleChangeListSize = (newSize: number) => {
        updateParams({
            listSize: newSize,
            page: 1, // page는 1로 리셋
            search: searchFilterValues.search || "title",
            searchtxt: searchFilterValues.searchtxt || "",
        });
        setCurrentPage(1);
    };

    // 파라미터 상태 동기화
    useEffect(() => {
        setListSize(params.listSize);
        setValue("search", params.search);
        setValue("searchtxt", params.searchtxt);
    }, [params.listSize, params.search, params.searchtxt, setListSize, setValue]);

    // currentPage 변경 시 URL 파라미터 업데이트
    const handleChangeCurrentPage = (page: number) => {
        updateParams({
            page: page,
        });
        setCurrentPage(page);
    };

    // 검색 하기
    const handleSearch = () => {
        const searchValue = searchFilterValues.search || "title";
        const searchTxtValue = searchFilterValues.searchtxt || "";

        updateParams({
            listSize: listSize,
            page: 1,
            search: searchValue,
            searchtxt: searchTxtValue,
        });
        setCurrentPage(1);
    };

    // 데이터 수정,삭제 중일 때 로딩 팝업 표시
    useEffect(() => {
        const isLoading =
            putBoardNoticeMutation.isPending || delBoardMutation.isPending || putBoardOrderMutation.isPending;
        setLoadingPop(isLoading, true);
        return () => setLoadingPop(false);
    }, [putBoardNoticeMutation.isPending, delBoardMutation.isPending, putBoardOrderMutation.isPending, setLoadingPop]);

    // 게시글 목록 조회
    useEffect(() => {
        if (configData) {
            const data = configData.data;
            setItems(data.board_list);
            setTotalPages(data.last_page);
            setTotalCount(data.total_count);
        } else {
            setItems([]);
            setTotalPages(1);
            setTotalCount(0);
        }
    }, [configData]);

    // 리스트 idx 값만 배열로 (전체 체크박스리스트 만들기)
    useEffect(() => {
        setCheckList(items.length > 0 ? items.map(item => item.idx) : []);
        setCheckedList([]);
    }, [items, setCheckList, setCheckedList]);

    useEffect(() => {
        const isLoading =
            putBoardNoticeMutation.isPending || delBoardMutation.isPending || putBoardOrderMutation.isPending;
        setLoadingPop(isLoading, true);
        return () => setLoadingPop(false);
    }, [putBoardNoticeMutation.isPending, delBoardMutation.isPending, putBoardOrderMutation.isPending, setLoadingPop]);

    // 페이지1 && 리스트 가져오기
    const resetAndRefetch = useCallback(() => {
        setCurrentPage(1);
        refetch();
    }, [refetch, setCurrentPage]);

    // listSize 변경시 리스트 가져오기
    useEffect(() => {
        resetAndRefetch();
    }, [listSize, resetAndRefetch]);

    // 공지설정 확인
    const handleConfirmNotice = (notice: string, idx: number, category: number) => {
        const txt = notice === "1" ? "공지 해제하시겠습니까?" : "공지 설정하시겠습니까?";
        setConfirmPop(true, txt, 2, () => handleNotice(notice, idx, category));
    };

    // 공지설정하기
    const handleNotice = (notice: string, idx: number, category: number) => {
        const txt = notice === "1" ? "공지 해제되었습니다." : "공지 설정되었습니다.";
        const body = { notice: notice === "1" ? "0" : "1", idx, category };
        putBoardNoticeMutation.mutate(body, {
            onSuccess: () => {
                setConfirmPop(true, txt, 1);
                refetch();
            },
        });
    };

    // 삭제 확인
    const handleConfirmDelete = () => {
        if (checkedList.length > 0) {
            setConfirmPop(true, "삭제 시 복구가 불가합니다. 삭제하시겠습니까?", 2, () => handleDelete());
        } else {
            setConfirmPop(true, "게시글을 선택해주세요.", 1);
        }
    };

    // 삭제하기
    const handleDelete = () => {
        if (!category) return;
        const body = { idx: checkedList, category: Number(category) };
        delBoardMutation.mutate(body, {
            onSuccess: () => {
                setConfirmPop(true, "삭제되었습니다.", 1);
                refetch();
            },
        });
    };

    // 순서 변경 처리
    const handleOrderChange = (idx: number, moveNum: number) => {
        if (!category) return;

        const body = {
            idx: idx,
            category: Number(category),
            b_num: moveNum,
        };

        putBoardOrderMutation.mutate(body, {
            onSuccess: () => {
                refetch();
            },
        });
    };

    return (
        <div className="bg-white p-[36px_40px] rounded-[8px] shadow-[0_0_8px_0_rgba(0,0,0,0.05)]">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-[12px]">
                    <p className="text-[24px] font-[600]">전체</p>
                    <p className="text-console font-[500]">총 {makeIntComma(totalCount)}개</p>
                </div>
                <div className="flex gap-[16px]">
                    <ListSizeSelect value={listSize} onChange={handleChangeListSize} options={options} />
                    <div className="flex gap-[8px]">
                        <SelectBox
                            list={listSearchTypes}
                            value={searchFilterValues.search}
                            onChange={value => setValue("search", value)}
                        />
                        <SearchInput {...register("searchtxt")} handleClick={handleSearch} />
                    </div>
                </div>
            </div>
            <div className="bg-[#dce1ec] flex items-center justify-between rounded-[5px] p-[20px_30px] mt-[20px]">
                <div className="flex items-center gap-[12px]">
                    <Checkbox checked={allCheck} onChange={e => handleAllCheck(e.currentTarget.checked)} />
                    <p className="text-[18px] font-[500]">
                        선택한 게시글 총 <span className="text-console">{makeIntComma(checkedNum)}</span>개
                    </p>
                </div>
                <button
                    type="button"
                    className="h-[30px] px-[10px] text-[14px] text-white bg-[#ff5463] rounded-[5px] font-[500]"
                    onClick={handleConfirmDelete}
                >
                    삭제
                </button>
            </div>
            {isInitialLoading ? (
                <div className="py-[50px]">
                    <LoadingSpinner console />
                </div>
            ) : items.length > 0 ? (
                <DraggableTable
                    thead={
                        <>
                            <colgroup>
                                <col width="80px" />
                                <col width="150px" />
                                <col width="35%" />
                                <col width="auto" />
                            </colgroup>
                            <thead>
                                <tr className="border-b border-[#ddd] bg-[#fbfbfc] text-[#666] text-[18px]">
                                    <th className="py-[15px] font-[500]"></th>
                                    <th className="py-[15px] font-[500]">번호</th>
                                    <th className="py-[15px] font-[500]">제목</th>
                                    <th className="py-[15px] font-[500]">작성자</th>
                                    <th className="py-[15px] font-[500]">작성일시</th>
                                    <th className="py-[15px] font-[500]">공지 설정</th>
                                    <th className="py-[15px] font-[500]">순서</th>
                                </tr>
                            </thead>
                        </>
                    }
                    items={items}
                    getItemId={item => item.idx}
                    renderRow={item => (
                        <>
                            <td>
                                <Checkbox
                                    checked={checkedList.includes(item.idx)}
                                    onChange={e => handleCheck(e.currentTarget.checked, item.idx)}
                                />
                            </td>
                            <td className="p-[15px_5px]">
                                {item.b_notice === "0" ? makeIntComma(item.num) : item.num}
                            </td>
                            <td className="p-[15px_5px] text-left">
                                <Link
                                    to={`/console/board/${category}/${item.idx}`}
                                    className="block w-full truncate text-[#222] font-[500] hover:underline"
                                >
                                    {item.b_title}
                                </Link>
                            </td>
                            <td className="p-[15px_5px]">{item.m_name}</td>
                            <td className="p-[15px_5px] text-[14px] text-[#999]">{item.b_reg_date}</td>
                            <td className="p-[15px_5px]">
                                <button
                                    type="button"
                                    className={`h-[30px] text-[14px] font-[500] rounded-[5px] px-[10px] text-[#666]  ${item.b_notice === "0" ? "bg-white border border-[#303644]" : "bg-[#f8f8f8]"}`}
                                    onClick={() => handleConfirmNotice(item.b_notice, item.idx, item.category)}
                                >
                                    {`공지 ${item.b_notice === "0" ? "설정" : "해제"}`}
                                </button>
                            </td>
                        </>
                    )}
                    handleOrderChange={handleOrderChange}
                />
            ) : (
                <NoData />
            )}
            <div className="relative mt-[45px] flex justify-center px-[260px]">
                {totalCount > 0 && (
                    <Pagination
                        totalPages={totalPages}
                        currentPage={currentPage}
                        pages={pages}
                        handleChangePage={handleChangeCurrentPage}
                    />
                )}
                <Link
                    to={`/console/board/${category}/create`}
                    className="w-[240px] leading-[50px] bg-[#192030] rounded-[5px] text-[18px] text-white font-[500] text-center absolute right-0 top-1/2 -translate-y-1/2"
                >
                    작성하기
                </Link>
            </div>
        </div>
    );
}
